import first from "lodash/first";
import { numify } from "numify";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import IconClock from "scmp-app/components/newsletter/newsletter-category-item-slideshow/icon-clock.svg";
import IconUser from "scmp-app/components/newsletter/newsletter-category-item-slideshow/icon-user.svg";
import Placeholder from "scmp-app/components/newsletter/newsletter-category-item-slideshow/placeholder.svg";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { NewsletterSignUpClickTriggerPoint } from "scmp-app/components/tracking/google-analytics-4/types";
import { useNewsletterLandingContext } from "scmp-app/pages/newsletters/contexts";
import type { newsletterItemNewsletter$key } from "scmp-app/queries/__generated__/newsletterItemNewsletter.graphql";

import {
  Container,
  Content,
  ContentHeader,
  Description,
  Flag,
  Footer,
  ImageContainer,
  Name,
  NumberOfSignedUpUsers,
  PlaceholderContainer,
  Preview,
  StyledBaseImage,
  StyledSignUpButton,
  Summary,
} from "./styles";

type Props = {
  className?: string;
  reference: newsletterItemNewsletter$key;
  triggerPoint?: NewsletterSignUpClickTriggerPoint;
};

export const NewsletterItem: FunctionComponent<Props> = ({ className, reference }) => {
  const newsletter = useFragment(
    graphql`
      fragment newsletterItemNewsletter on Newsletter {
        name
        numberOfSignedUpUsers
        images {
          title
          url
        }
        summary {
          text
        }
        description {
          text
        }
        shortDescription {
          text
        }
        ...entityLink
        ...signUpButtonNewsletter
      }
    `,
    reference,
  );

  const { isEmbed } = useNewsletterLandingContext();

  const handlePreviewClick = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "nl",
      customized_parameters: {
        newsletter_name: newsletter.name,
      },
      subcategory: "preview",
    });
  }, [newsletter.name]);

  const renderImage = useCallback(() => {
    const newsletterImage = first(newsletter.images ?? []);

    if (notEmpty(newsletterImage?.url)) {
      return (
        <ImageContainer>
          <StyledBaseImage
            $aspectRatio="3/2"
            $objectFit="cover"
            alt={newsletterImage?.title ?? newsletter.name ?? ""}
            src={newsletterImage.url}
          />
          {newsletter.shortDescription?.text && <Flag>{newsletter.shortDescription?.text}</Flag>}
        </ImageContainer>
      );
    }
    return (
      <PlaceholderContainer>
        <Placeholder />
      </PlaceholderContainer>
    );
  }, [newsletter.images, newsletter.name, newsletter.shortDescription?.text]);

  return (
    <Container className={className}>
      {renderImage()}

      <Content>
        <ContentHeader>
          {newsletter.summary?.text && (
            <Summary>
              <IconClock />
              {newsletter.summary?.text}
            </Summary>
          )}
          {newsletter.numberOfSignedUpUsers && (
            <NumberOfSignedUpUsers>
              <IconUser />
              {numify(newsletter.numberOfSignedUpUsers)}
            </NumberOfSignedUpUsers>
          )}
        </ContentHeader>
        {newsletter.name && <Name>{newsletter.name}</Name>}

        {newsletter.description?.text && <Description>{newsletter.description?.text}</Description>}
      </Content>
      <Footer>
        <StyledSignUpButton reference={[newsletter]} />
        <Preview
          onClick={handlePreviewClick}
          reference={newsletter}
          shallow
          target={isEmbed ? "_blank" : undefined}
        >
          Preview
        </Preview>
      </Footer>
    </Container>
  );
};

NewsletterItem.displayName = "NewsletterItem";
